import { useState } from 'react'
import { ChevronRight, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MONO, fmt, fmtPct } from './format'
import { IncomeCaptionRow, IncomeSubtotalRows } from './IncomeSubtotalRows'
import type { PLSection, PLTableProps, SectionId } from '../types'

const DEC_NOTE =
  'Dec 2025 COGS carries a ~$1.68M year-end true-up (a one-off) — the single largest item in the window. It pushes operating profit negative for that month; the YTD 2026 column is unaffected.'

/**
 * The P&L statement. Fixed statement order (never sortable): Income, COGS, Gross
 * profit, Operating expenses, Operating profit, Other expenses, Net profit, with
 * margin rows beneath each profit line. Sections collapse by default; the chevron
 * expands the account detail. Expense lines render as positive magnitudes tinted
 * red. A sticky first column, one MONO column per month, and a bold YTD column.
 * Under Income (always visible), the schema-v2 business unit › department
 * subtotals; the Income accounts expand beneath them exactly as before.
 */
export function PLTable({ sections, totals, months, currency, ytdYear, unitSubtotals, departmentSubtotals }: PLTableProps) {
  const [open, setOpen] = useState<Record<SectionId, boolean>>({
    income: false,
    cogs: false,
    opex: false,
    otherExpense: false,
  })
  const toggle = (id: SectionId) => setOpen((s) => ({ ...s, [id]: !s[id] }))

  const decIdx = months.findIndex((m) => m.startsWith('Dec-'))
  const ytdLabel = `YTD '${String(ytdYear % 100).padStart(2, '0')}`
  const longMonth = (key: string) => {
    const [mon, yy] = key.split('-')
    return `${mon} 20${yy}`
  }
  const periodLabel = months.length ? `${longMonth(months[0])} – ${longMonth(months[months.length - 1])}` : ''
  const tableMinWidth = 300 + months.length * 82 + 120
  const sec = (id: SectionId) => sections.find((s) => s.id === id)
  const marginYtd = (num: number) => (totals.revenueYtd ? (num / totals.revenueYtd) * 100 : 0)
  // Operating margin isn't stored; it's derived from the absolute totals, same as
  // the Consolidated view recomputes every margin from absolutes.
  const operatingMarginPct = totals.operatingProfit.map((v, i) =>
    totals.revenue[i] ? (v / totals.revenue[i]) * 100 : 0,
  )

  // ---- cell helpers --------------------------------------------------------
  const dash = <span className="text-stone-300 dark:text-stone-600">—</span>
  const moneyCell = (
    v: number,
    i: number,
    opts: { red?: boolean; strong?: boolean; muted?: boolean; signed?: boolean; emerald?: boolean; flag?: boolean } = {},
  ) => {
    const flagged = i === decIdx && opts.flag // the Dec-25 COGS true-up column
    let tone = 'text-stone-700 dark:text-stone-200'
    if (opts.red) tone = 'text-red-600 dark:text-red-400'
    else if (opts.muted) tone = 'text-stone-500 dark:text-stone-400'
    else if (opts.signed) tone = v < 0 ? 'text-red-600 dark:text-red-400' : opts.emerald ? 'text-emerald-700 dark:text-emerald-400' : 'text-stone-900 dark:text-stone-100'
    return (
      <td
        key={i}
        title={flagged ? DEC_NOTE : undefined}
        className={cn(
          'whitespace-nowrap px-2 py-2 text-right text-xs',
          MONO,
          opts.strong && 'font-semibold',
          flagged && 'bg-champagne-50/50 dark:bg-champagne-500/10',
          tone,
        )}
      >
        {v === 0 ? dash : fmt(v, currency)}
      </td>
    )
  }
  const ytdCell = (v: number, opts: { strong?: boolean; red?: boolean; signed?: boolean; emerald?: boolean } = {}) => {
    let tone = 'text-stone-800 dark:text-stone-100'
    if (opts.red) tone = 'text-red-600 dark:text-red-400'
    else if (opts.signed) tone = v < 0 ? 'text-red-600 dark:text-red-400' : opts.emerald ? 'text-emerald-700 dark:text-emerald-400' : 'text-stone-900 dark:text-stone-100'
    return (
      <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs', MONO, opts.strong ? 'font-bold' : 'font-semibold', tone)}>
        {v === 0 ? dash : fmt(v, currency)}
      </td>
    )
  }
  const pctCell = (p: number, i: number) => (
    <td key={i} className={cn('whitespace-nowrap px-2 py-1.5 text-right text-[11px]', MONO, p < 0 ? 'text-red-500 dark:text-red-400' : 'text-stone-400 dark:text-stone-500')}>
      {totals.revenue[i] ? fmtPct(p) : dash}
    </td>
  )

  // ---- section (expandable) rows ------------------------------------------
  const sectionRows = (id: SectionId, red: boolean) => {
    const s = sec(id) as PLSection | undefined
    if (!s) return null
    const isOpen = open[id]
    const flag = id === 'cogs' // only the COGS column carries the Dec-25 true-up marker
    const isIncome = id === 'income'
    return (
      <>
        <tr data-section={id} className="border-t border-stone-100 dark:border-stone-800/70">
          <th
            scope="row"
            className="sticky left-0 z-10 bg-white px-4 py-2 text-left dark:bg-stone-900"
          >
            <button
              type="button"
              onClick={() => toggle(id)}
              aria-expanded={isOpen}
              disabled={s.accounts.length === 0}
              className={cn(
                'inline-flex items-center gap-1.5 text-sm font-medium text-stone-800 dark:text-stone-100',
                s.accounts.length === 0 ? 'cursor-default opacity-70' : 'hover:text-champagne-700 dark:hover:text-champagne-300',
              )}
            >
              <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform', isOpen && 'rotate-90', s.accounts.length === 0 && 'invisible')} />
              {s.label}
              {s.accounts.length > 0 && (
                <span className="text-[10px] font-normal text-stone-400 dark:text-stone-500">
                  {s.accounts.length}
                </span>
              )}
            </button>
          </th>
          {s.total.map((v, i) => moneyCell(v, i, { red, strong: true, flag }))}
          {ytdCell(s.totalYtd, { strong: false, red })}
        </tr>
        {isIncome && (
          <IncomeSubtotalRows
            units={unitSubtotals}
            departments={departmentSubtotals}
            accountNames={Object.fromEntries(s.accounts.map((a) => [a.id, a.name]))}
            months={months}
            currency={currency}
          />
        )}
        {isIncome && isOpen && unitSubtotals && s.accounts.length > 0 && (
          <IncomeCaptionRow colSpan={months.length + 1}>By account</IncomeCaptionRow>
        )}
        {isOpen &&
          s.accounts.map((a) => (
            <tr key={a.id} data-account-id={a.id} className="border-t border-stone-50 dark:border-stone-800/40">
              <th scope="row" className="sticky left-0 z-10 bg-white py-1.5 pl-9 pr-4 text-left dark:bg-stone-900">
                <span className="block truncate text-xs font-normal text-stone-500 dark:text-stone-400" title={a.name}>
                  {a.name}
                </span>
              </th>
              {a.values.map((v, i) => moneyCell(v, i, { red, muted: !red, flag }))}
              <td className={cn('whitespace-nowrap px-3 py-1.5 text-right text-xs font-medium', MONO, red ? 'text-red-600/90 dark:text-red-400/90' : 'text-stone-600 dark:text-stone-300')}>
                {a.ytd === 0 ? dash : fmt(a.ytd, currency)}
              </td>
            </tr>
          ))}
      </>
    )
  }

  // ---- computed subtotal / total rows -------------------------------------
  const computedRow = (
    label: string,
    values: number[],
    ytd: number,
    opts: { emerald?: boolean; strong?: boolean } = {},
  ) => (
    <tr className={cn('border-t-2 border-stone-200 dark:border-stone-700', opts.strong && 'bg-stone-50/70 dark:bg-stone-800/40')}>
      <th scope="row" className={cn('sticky left-0 z-10 px-4 py-2 text-left text-sm', opts.strong ? 'bg-stone-50/70 font-bold text-stone-900 dark:bg-stone-800/40 dark:text-white' : 'bg-white font-semibold text-stone-800 dark:bg-stone-900 dark:text-stone-100')}>
        {label}
      </th>
      {values.map((v, i) => moneyCell(v, i, { strong: true, signed: true, emerald: opts.emerald }))}
      {ytdCell(ytd, { strong: opts.strong, signed: true, emerald: opts.emerald })}
    </tr>
  )

  const marginRow = (label: string, values: number[], ytd: number) => (
    <tr className="border-t border-stone-50 dark:border-stone-800/40">
      <th scope="row" className="sticky left-0 z-10 bg-white py-1.5 px-4 text-left text-[11px] font-normal italic text-stone-400 dark:bg-stone-900 dark:text-stone-500">
        {label}
      </th>
      {values.map((p, i) => pctCell(p, i))}
      <td className={cn('whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-medium', MONO, ytd < 0 ? 'text-red-500 dark:text-red-400' : 'text-stone-500 dark:text-stone-400')}>
        {totals.revenueYtd ? fmtPct(ytd) : dash}
      </td>
    </tr>
  )

  return (
    <div className="rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-5">
        <div>
          <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Statement · {periodLabel}</h3>
          <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
            Accrual basis · expenses shown as positive magnitudes · click a section to expand its accounts
          </p>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm" style={{ minWidth: tableMinWidth }}>
          <thead>
            <tr className="border-y border-stone-100 dark:border-stone-800">
              <th className="sticky left-0 z-20 bg-white px-4 py-2 text-left text-xs font-medium text-stone-500 dark:bg-stone-900 dark:text-stone-400">
                Account
              </th>
              {months.map((m, i) => (
                <th key={m} className="whitespace-nowrap px-2 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">
                  <span className="inline-flex items-center gap-1">
                    {m}
                    {i === decIdx && (
                      <span title={DEC_NOTE} className="text-champagne-500 dark:text-champagne-400">
                        <Info className="h-3 w-3" />
                      </span>
                    )}
                  </span>
                </th>
              ))}
              <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold text-stone-600 dark:text-stone-300">
                {ytdLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {sectionRows('income', false)}
            {sectionRows('cogs', true)}
            {computedRow('Gross profit', totals.grossProfit, totals.grossProfitYtd)}
            {marginRow('Gross margin', totals.grossMarginPct, marginYtd(totals.grossProfitYtd))}
            {sectionRows('opex', true)}
            {computedRow('Operating profit', totals.operatingProfit, totals.operatingProfitYtd)}
            {marginRow('Operating margin', operatingMarginPct, marginYtd(totals.operatingProfitYtd))}
            {sectionRows('otherExpense', true)}
            {computedRow('Net profit', totals.netProfit, totals.netProfitYtd, { emerald: true, strong: true })}
            {marginRow('Net margin', totals.netMarginPct, marginYtd(totals.netProfitYtd))}
          </tbody>
        </table>
      </div>

      {totals.fullPeriod && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-stone-100 px-5 py-3 text-[11px] text-stone-400 dark:border-stone-800 dark:text-stone-500">
          <span>
            Full {months.length}-month period · Revenue <span className={cn(MONO, 'text-stone-600 dark:text-stone-300')}>{fmt(totals.fullPeriod.revenue, currency)}</span>
          </span>
          <span>
            Gross profit <span className={cn(MONO, 'text-stone-600 dark:text-stone-300')}>{fmt(totals.fullPeriod.grossProfit, currency)}</span>
          </span>
          <span>
            Net profit <span className={cn(MONO, totals.fullPeriod.netProfit < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400')}>{fmt(totals.fullPeriod.netProfit, currency)}</span>
          </span>
        </div>
      )}
    </div>
  )
}
